import { apiFetch } from './client.js';
import type { StoredOrganisation } from './organisation.api.js';

/**
 * Restore an earlier settings version from a section's History (DEV-142).
 * Every call names the stored version only; the server reads its values
 * itself, so a masked or edited value on this screen can never be what is
 * restored. A dry run answers what the restore would change, for the review.
 */

export interface RestoreChange { key: string; label: string; before: unknown; after: unknown }

export interface OrganisationRestorePreview {
  version: number;
  expectedVersion: number;
  changes: RestoreChange[];
  /** Set when the restore re-addresses the daily commission transfer (Verification of Payee). */
  commissionRecipient?: { before: string; after: string };
}

export interface TreasuryRestorePreview {
  version: number;
  expectedVersion: number;
  changes: RestoreChange[];
  /** The restore would switch the commission transfer to LIVE: it must be confirmed. */
  switchesToLive: boolean;
  /** A different revenue or client-money account is checked at GuruPay on restore. */
  verifiesRevenueAccount: boolean;
}

/** A restore waiting for (or decided by) a second approver: risk scoring, transaction monitoring. */
export interface RestoreRequest {
  id: string;
  section: string;
  targetVersion: string;
  targetLabel: string;
  baseVersion: string;
  reason: string;
  proposedBy: string;
  proposedByName: string | null;
  proposedAt: string;
  /** PENDING and APPLYING are open; APPLYING is being put in force. */
  status: 'PENDING' | 'APPLYING' | 'APPLIED' | 'FAILED' | 'EXPIRED' | 'REJECTED';
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
  resultVersion: string | null;
  /** Why it FAILED or EXPIRED. */
  failureDetail: string | null;
  changes: RestoreChange[];
}

export interface FourEyesRestorePreview {
  version: string;
  currentVersion: string;
  targetLabel: string;
  changes: RestoreChange[];
  needsSecondApprover: boolean;
}

export const organisationRestoreApi = {
  preview: (version: number, expectedVersion: number): Promise<OrganisationRestorePreview> =>
    apiFetch('/admin/settings/organisation/restore', { method: 'POST', body: { version, expectedVersion, dryRun: true } }),
  restore: (input: { version: number; expectedVersion: number; reason: string; confirmCommissionRecipientChange?: boolean }): Promise<StoredOrganisation & { changed: boolean; restoredFromVersion: number }> =>
    apiFetch('/admin/settings/organisation/restore', { method: 'POST', body: input }),
};

export const treasuryRestoreApi = {
  preview: (provider: string, version: number, expectedVersion: number): Promise<TreasuryRestorePreview> =>
    apiFetch(`/admin/settings/treasury/${provider}/restore`, { method: 'POST', body: { version, expectedVersion, dryRun: true } }),
  restore: (provider: string, input: { version: number; expectedVersion: number; reason: string; confirmLiveMode?: boolean }): Promise<unknown> =>
    apiFetch(`/admin/settings/treasury/${provider}/restore`, { method: 'POST', body: input }),
};

/** The four-eyes endpoints of one section, under its own path. */
export function fourEyesRestoreApi(base: string) {
  return {
    preview: (version: string): Promise<FourEyesRestorePreview> =>
      apiFetch(`${base}/restore`, { method: 'POST', body: { version, dryRun: true } }),
    /**
     * Four eyes: a PENDING request. One approver (decision 18.10, today): the
     * restore itself, answered with its status (APPLIED). `discardDraft`:
     * monitoring, the writer agrees the draft in progress is replaced.
     */
    propose: (version: string, reason: string, discardDraft = false): Promise<{ status: RestoreRequest['status']; targetLabel: string }> =>
      apiFetch(`${base}/restore`, { method: 'POST', body: { version, reason, ...(discardDraft ? { discardDraft: true } : {}) } }),
    list: (): Promise<RestoreRequest[]> => apiFetch(`${base}/restore-requests`),
    /** `discardDraft`: monitoring, the approver agrees the draft in progress is replaced (else 409 DRAFT_IN_PROGRESS). */
    approve: (id: string, options: { reason?: string; discardDraft?: boolean } = {}): Promise<RestoreRequest> =>
      apiFetch(`${base}/restore-requests/${id}/approve`, { method: 'POST', body: options }),
    reject: (id: string, reason: string): Promise<RestoreRequest> =>
      apiFetch(`${base}/restore-requests/${id}/reject`, { method: 'POST', body: { reason } }),
  };
}

export type FourEyesRestoreApi = ReturnType<typeof fourEyesRestoreApi>;

export const RISK_SCORING_RESTORE = '/admin/risk-scoring/model';
export const MONITORING_RESTORE = '/admin/settings/transaction-monitoring';

export const restoreRequestsKey = (base: string): readonly unknown[] => ['settings-restore-requests', base];

/** Settings → Pricing: publish an earlier price again as a new version, effective now. */
export const feeRestoreApi = {
  restore: (scheduleId: string, reason: string): Promise<unknown> =>
    apiFetch(`/admin/fees/schedules/${scheduleId}/restore`, { method: 'POST', body: { reason } }),
};

/** Settings → Compliance → Onboarding policy: make an archived version active again. */
export const policyRestoreApi = {
  restore: (versionId: string, reason: string): Promise<unknown> =>
    apiFetch(`/admin/onboarding/policy/versions/${versionId}/restore`, { method: 'POST', body: { reason } }),
};
